// src/components/Students/StudentLedger.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  PictureAsPdf as PdfIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { studentAPI, feeAPI, academicAPI } from "../../services/api";
import { useParams, useNavigate } from "react-router-dom";
import StudentFeeCard from "./StudentFeeCard";
import StudentFeePdfButtons from "./student-fee-pdf/student-fee-pdf-buttons";

const StudentLedger = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [studentDetails, setStudentDetails] = useState(null);
  const [student, setStudent] = useState(null);
  const [feeGroups, setFeeGroups] = useState([]);
  const [terms, setTerms] = useState([]);
  const [feeCardData, setFeeCardData] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    fee_group_id: "",
    status: "",
    term_id: ""
  });
  const [downloading, setDownloading] = useState(false);
  const [showFeeCard, setShowFeeCard] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (studentId) {
      fetchTerms();
      fetchFeeGroups();
      fetchStudentLedger();
      fetchFeeCardData();
    }
  }, [studentId]);

  const fetchTerms = async () => {
    try {
      const response = await academicAPI.getTerms();
      setTerms(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  const fetchFeeGroups = async () => {
    try {
      const response = await feeAPI.getGroups();
      setFeeGroups(response.data.data || response.data);
    } catch (error) {
      console.error("Error fetching fee groups:", error);
    }
  };

  const fetchFeeCardData = async () => {
    try {
      const params = { student_id: studentId };
      const response = await feeAPI.getStudentFeeCard(params);
      setFeeCardData(response.data);
    } catch (error) {
      console.error("Error fetching fee card data:", error);
    }
  };

  const fetchStudentLedger = async (filterParams = filters) => {
    try {
      setLoading(true);
      const params = {};
      if (filterParams.fee_group_id) params.fee_group_id = filterParams.fee_group_id;
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.term_id) params.term_id = filterParams.term_id;

      const response = await studentAPI.getLedger(studentId, params);
      console.log("Student ledger data:", response.data);
      setStudentDetails(response.data);
      setStudent(response.data.student);
    } catch (error) {
      console.error("Error fetching student ledger:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    fetchStudentLedger(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = { fee_group_id: "", status: "", term_id: "" };
    setFilters(clearedFilters);
    fetchStudentLedger(clearedFilters);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      setErrorMessage("");
      
      const params = {};
      if (filters.fee_group_id) params.fee_group_id = filters.fee_group_id;
      if (filters.status) params.status = filters.status;
      if (filters.term_id) params.term_id = filters.term_id;

      console.log("Downloading PDF with params:", params);
      const response = await studentAPI.downloadLedgerPdf(studentId, params);
      
      console.log("Response received:", response);
      console.log("Response headers:", response.headers);
      console.log("Response data type:", typeof response.data);
      console.log("Response data size:", response.data?.size);
      console.log("Response data constructor:", response.data?.constructor?.name);
      
      // Check if response is an error (JSON response instead of PDF)
      if (response.data instanceof Blob) {
        // Check if it's actually a PDF
        const contentType = response.headers['content-type'];
        console.log("Content-Type:", contentType);
        
        if (!contentType || !contentType.includes('pdf')) {
          // Try to read as text to see the error
          const text = await response.data.text();
          console.error("Response is not PDF:", text);
          throw new Error("Server returned an error: " + text);
        }
      }
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        throw new Error("Empty response from server");
      }
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `student_ledger_${student.registration_no}_${new Date().toISOString().split("T")[0]}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      console.error("Error response:", error.response);
      
      let errorMsg = "Failed to download PDF. Please try again.";
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (!studentDetails || !student) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary">
          Student not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/students")}
          sx={{ mt: 2 }}
        >
          Back to Students
        </Button>
      </Box>
    );
  }

  const hasActiveFilters = filters.fee_group_id || filters.status || filters.term_id;
  const summary = studentDetails.summary || {
    total_allocated: 0,
    total_paid: 0,
    total_balance: 0,
  };

  return (
    <Box>
      {/* Error Alert */}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}

      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate("/students")} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Student Fee Ledger
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {student.last_name} {student.first_name} - {student.registration_no || student.lin}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} className="no-print">
          <Tooltip title="Export as PDF">
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPdf}
              disabled={downloading}
              size={isMobile ? "small" : "medium"}
            >
              {downloading ? "Exporting..." : "Export PDF"}
            </Button>
          </Tooltip>
          <Tooltip title="Print">
            {/* <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              size={isMobile ? "small" : "medium"}
            >
              Print
            </Button> */}
          </Tooltip>
          {feeCardData && <StudentFeePdfButtons feeData={feeCardData} studentId={studentId} />}
        </Stack>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }} className="no-print">
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FilterIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.fee_group_id}
                  label="Fee Group"
                  onChange={(e) => handleFilterChange("fee_group_id", e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Fee Groups</MenuItem>
                  {feeGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange("status", e.target.value)}                  
                  displayEmpty
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.term_id}
                  label="Term"
                  onChange={(e) => handleFilterChange("term_id", e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Terms</MenuItem>
                  {terms.map((term) => (
                    <MenuItem key={term.id} value={term.id}>
                      {term.name} {term.year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  fullWidth
                >
                  Clear Filters
                </Button>
              )}
            </Grid>
          </Grid>

          {hasActiveFilters && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Active Filters:</strong>
              {filters.fee_group_id && ` Fee Group: ${feeGroups.find(g => g.id == filters.fee_group_id)?.name}`}
              {filters.status && ` | Status: ${filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}`}
              {filters.term_id && ` | Term: ${terms.find(t => t.id == filters.term_id)?.name}`}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Student Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {student.picture_url && (
              <Grid item xs={12} md={2}>
                <Box textAlign="center">
                  <img 
                    src={student.picture_url} 
                    alt={`${student.last_name} ${student.first_name}`}
                    style={{ 
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      border: `3px solid ${theme.palette.primary.main}`,
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={student.picture_url ? 2.5 : 3}>
              <Typography variant="caption" color="text.secondary">
                Register No
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {student.registration_no || student.lin}
              </Typography>
            </Grid>
            <Grid item xs={12} md={student.picture_url ? 2.5 : 3}>
              <Typography variant="caption" color="text.secondary">
                Class
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {student.class_stream_name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={student.picture_url ? 2.5 : 3}>
              <Typography variant="caption" color="text.secondary">
                Guardian
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {student.guardian_name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={student.picture_url ? 2.5 : 3}>
              <Typography variant="caption" color="text.secondary">
                Guardian Phone
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {student.guardian_phone}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "info.50", border: "1px solid", borderColor: "info.main" }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                Total Allocated
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="info.main">
                UGX {parseFloat(summary.total_allocated).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "success.50", border: "1px solid", borderColor: "success.main" }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                Total Amount Paid
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                UGX {parseFloat(summary.total_paid).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              bgcolor: summary.total_balance === 0 ? "success.50" : "error.50",
              border: "1px solid",
              borderColor: summary.total_balance === 0 ? "success.main" : "error.main",
            }}
          >
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                Outstanding Balance
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={summary.total_balance === 0 ? "success.main" : "error.main"}
              >
                UGX {parseFloat(summary.total_balance).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payment History Table */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <ReceiptIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Payment History</Typography>
          </Box>

          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Fee Group</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">
                    Allocated
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">
                    Amount Paid
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Payment Method</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">
                    Running Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">
                    Balance
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(studentDetails.payment_balances || []).length > 0 ? (
                  studentDetails.payment_balances.map((payment, index) => {
                    const feePayment = student.fee_payments.find(
                      (fp) => fp.id === payment.payment_id
                    );

                    return (
                      <TableRow key={`${payment.payment_id || 'pending'}-${index}`} hover>
                        <TableCell>
                          {payment.payment_date ? (
                            new Date(payment.payment_date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No payment
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.fee_group_name}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium" color="info.main">
                            UGX {parseFloat(payment.allocated).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            color="success.main"
                          >
                            UGX {parseFloat(payment.amount).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {feePayment?.payment_method ? (
                            <Chip
                              label={
                                feePayment.payment_method
                                  .replace("_", " ")
                                  .toUpperCase()
                              }
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell>
                          {feePayment?.account ? (
                            <Box>
                              <Typography variant="body2">
                                {feePayment.account.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {feePayment.account.account_number}
                              </Typography>
                            </Box>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            UGX {parseFloat(payment.running_paid).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              payment.balance_after_payment === 0
                                ? "success.main"
                                : "warning.main"
                            }
                          >
                            UGX{" "}
                            {parseFloat(payment.balance_after_payment).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.balance_after_payment > 0 ? "Pending" : "Paid"}
                            size="small"
                            color={payment.balance_after_payment > 0 ? "warning" : "success"}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No payment records found
                        {hasActiveFilters && " for the selected filters"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default StudentLedger;
